import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtDate, fmtDateTime, fmtMoney, fmtNumber } from '../lib/format';
import { useCan } from '../lib/auth';
import { Button, Callout, Dialog, Empty, ErrorState, Field, Input, Ltr, Select, SkeletonRows, useToast } from '../components/ui';
import { IconDownload } from '../components/icons';
import { useOpenDocument } from '../components/Layout';

type Status = 'uncounted' | 'negative' | 'out' | 'low' | 'ok';
type Item = {
  productId: number;
  sku: string;
  name: string;
  category: string | null;
  unit: string | null;
  unitsPerPack: number | null;
  qty: number | null;
  min: number | null;
  autoMin: number | null;
  perMonth: number;
  daysLeft: number | null;
  status: Status;
  cost: number | null;
  value: number | null;
  lastMove: string | null;
};
type Inventory = {
  items: Item[];
  counts: Record<Status, number>;
  value: number | null;
  valueMissing: number;
  paceDays: number;
  unmatched: Array<{ text: string; qty: number; documents: number; lastDocumentId: number }>;
  openCount: { id: number; name: string } | null;
};
type Movement = {
  id: number;
  kind: 'count' | 'sale' | 'return' | 'receipt' | 'manual';
  qty: number;
  balance: number;
  documentId: number | null;
  docType: string | null;
  docNumber: string | null;
  customerName: string | null;
  countName: string | null;
  note: string | null;
  userName: string | null;
  at: string;
};
type Filter = 'all' | 'alert' | 'out' | 'negative' | 'uncounted';

export const qtyText = (n: number | null) => (n == null ? '—' : Number.isInteger(n) ? fmtNumber(n) : n.toLocaleString('he-IL', { maximumFractionDigits: 2 }));
const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

const STATUS: Record<Status, { label: string; cls: string }> = {
  uncounted: { label: 'לא נספר', cls: 'outline' },
  negative: { label: 'במינוס', cls: 'bad' },
  out: { label: 'אזל', cls: 'bad' },
  low: { label: 'עומד להיגמר', cls: 'warn' },
  ok: { label: 'תקין', cls: 'ok' },
};
const KIND: Record<Movement['kind'], string> = { count: 'ספירה', sale: 'יצא ללקוח', return: 'חזר (זיכוי)', receipt: 'קבלת סחורה', manual: 'תיקון ידני' };
const DOC: Record<string, string> = { tax_invoice: 'חשבונית מס', tax_invoice_receipt: 'חשבונית מס/קבלה', delivery_note: 'תעודת משלוח', credit_note: 'חשבונית זיכוי' };

/** The stock of every catalog product: quantity, value at cost, and what is about to run out. */
export function InventoryPage() {
  const can = useCan();
  const showCost = can('profit.view');
  const { data, error, refetch, isLoading } = useQuery({ queryKey: ['inventory'], queryFn: () => api<Inventory>('/api/inventory') });
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [open, setOpen] = useState<Item | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  const categories = useMemo(() => [...new Set((data?.items ?? []).map((i) => i.category).filter((c): c is string => Boolean(c)))].sort(), [data]);
  const rows = (data?.items ?? []).filter((i) => {
    if (filter === 'alert' && i.status !== 'low' && i.status !== 'out') return false;
    if (filter === 'out' && i.status !== 'out') return false;
    if (filter === 'negative' && i.status !== 'negative') return false;
    if (filter === 'uncounted' && i.status !== 'uncounted') return false;
    if (category && i.category !== category) return false;
    const s = q.trim().toLowerCase();
    return !s || `${i.name} ${i.sku}`.toLowerCase().includes(s);
  });
  // what runs out first on top when looking at the alerts
  if (filter === 'alert') rows.sort((a, b) => (a.daysLeft ?? -1) - (b.daysLeft ?? -1));
  const neverCounted = data && data.counts.uncounted === data.items.length;

  const startCount = async () => {
    try {
      const r = await api<{ id: number }>('/api/inventory/counts', { body: {} });
      navigate(`/inventory/count/${r.id}`);
    } catch (e) {
      toast.error(e);
    }
  };
  const cols = showCost ? 9 : 7;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>מלאי</h1>
          <div className="sub">כל מוצרי הקטלוג, מלאי אחד לכל המחסנים. כל תעודת משלוח או חשבונית שיוצאת מורידה מהמלאי (חשבונית שמחייבת תעודות לא מורידה שוב; חשבונית שבוטלה בזיכוי מחזירה). "עומד להיגמר": מתחת למינימום שקבעת, או בלי מינימום — פחות משבועיים של מכירות לפי קצב {data?.paceDays ?? 90} הימים האחרונים.</div>
        </div>
        <div className="row wrap">
          {data?.openCount ? (
            <Link to={`/inventory/count/${data.openCount.id}`} className="btn primary">המשך ספירה: {data.openCount.name}</Link>
          ) : (
            <Button variant={neverCounted ? 'primary' : 'default'} onClick={startCount}>{neverCounted ? 'ספירת פתיחה' : 'ספירת מלאי'}</Button>
          )}
          <Link to="/inventory/receipts" className="btn">קבלת סחורה</Link>
          <Link to="/inventory/counts" className="btn">ספירות קודמות</Link>
          <a className="btn" href="/api/inventory/export"><IconDownload /> Excel</a>
        </div>
      </div>

      <div className="stack">
        {neverCounted ? (
          <Callout tone="info" title="מתחילים בספירת פתיחה">
            עוברים במחסן עם הטלפון ומקלידים כמות ליד כל מוצר (נשמר תוך כדי, אפשר לעצור ולהמשיך). באישור הספירה הכמויות נכנסות למלאי. מוצר שלא נספר נשאר "לא נספר".
          </Callout>
        ) : null}

        {data ? (
          <section className="kpis" aria-label="סיכום">
            {showCost ? (
              <div className="kpi">
                <div className="kpi-label">שווי המלאי (לפי עלות)</div>
                <div className="kpi-value">{fmtMoney(data.value, { round: true })}</div>
                <div className="kpi-meta">{data.valueMissing ? `${fmtNumber(data.valueMissing)} מוצרים במלאי בלי עלות — לא בשווי` : 'לכל המוצרים במלאי יש עלות'}</div>
              </div>
            ) : null}
            <button className="kpi kpi-button" onClick={() => setFilter('alert')}>
              <div className="kpi-label">עומד להיגמר / אזל</div>
              <div className="kpi-value" style={data.counts.low + data.counts.out ? { color: 'var(--warn)' } : undefined}>{fmtNumber(data.counts.low + data.counts.out)}</div>
              <div className="kpi-meta">{fmtNumber(data.counts.out)} אזלו</div>
            </button>
            <button className="kpi kpi-button" onClick={() => setFilter('negative')}>
              <div className="kpi-label">במינוס</div>
              <div className="kpi-value" style={data.counts.negative ? { color: 'var(--bad)' } : undefined}>{fmtNumber(data.counts.negative)}</div>
              <div className="kpi-meta">יצא יותר ממה שנספר — לבדוק בספירה הבאה</div>
            </button>
            <button className="kpi kpi-button" onClick={() => setFilter('uncounted')}>
              <div className="kpi-label">לא נספרו</div>
              <div className="kpi-value">{fmtNumber(data.counts.uncounted)}</div>
              <div className="kpi-meta">מתוך {fmtNumber(data.items.length)} מוצרים</div>
            </button>
          </section>
        ) : null}

        <section className="panel">
          <div className="panel-head row wrap" style={{ gap: 10 }}>
            <div className="segmented" role="group" aria-label="סינון">
              {(
                [
                  ['all', 'הכל'],
                  ['alert', 'עומד להיגמר'],
                  ['out', 'אזל'],
                  ['negative', 'במינוס'],
                  ['uncounted', 'לא נספר'],
                ] as Array<[Filter, string]>
              ).map(([k, label]) => (
                <button key={k} className={filter === k ? 'active' : ''} onClick={() => setFilter(k)}>{label}</button>
              ))}
            </div>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש מוצר / מק״ט" style={{ maxWidth: 220 }} aria-label="חיפוש" />
            <Select value={category} onChange={(e) => setCategory(e.target.value)} style={{ maxWidth: 180 }} aria-label="קטגוריה">
              <option value="">כל הקטגוריות</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
            <span className="spacer" />
            <span className="muted small">{fmtNumber(rows.length)} מוצרים</span>
          </div>
          {error ? (
            <ErrorState error={error} onRetry={() => refetch()} />
          ) : (
            <div className="table-wrap" style={{ maxHeight: 'calc(100vh - 330px)', overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>מוצר</th>
                    <th className="num">במלאי</th>
                    <th className="num hide-sm" title="התראה מתחת לכמות הזו">מינימום</th>
                    <th className="num hide-sm">מכירות בחודש</th>
                    <th className="num hide-sm" title="לפי קצב המכירות">יספיק ל-</th>
                    <th>מצב</th>
                    {showCost ? <th className="num hide-sm">עלות</th> : null}
                    {showCost ? <th className="num">שווי</th> : null}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {isLoading && !data ? (
                    <SkeletonRows rows={10} cols={cols} />
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={cols}><Empty title={filter === 'all' ? 'אין מוצרים' : 'אין מוצרים במצב הזה'} /></td></tr>
                  ) : (
                    rows.map((i) => (
                      <tr key={i.productId} className="clickable" onClick={() => setOpen(i)}>
                        <td className="primary">
                          {i.name}
                          <span className="sub"><Ltr>{i.sku}</Ltr>{i.category ? ` · ${i.category}` : ''}{i.unitsPerPack ? ` · ${i.unitsPerPack} יח׳ בחבילה` : ''}</span>
                        </td>
                        <td className="num tabular strong">{qtyText(i.qty)}</td>
                        <td className="num tabular hide-sm">{i.min != null ? qtyText(i.min) : i.autoMin != null ? <span className="muted" title="אוטומטי: שבועיים של מכירות">{qtyText(i.autoMin)}</span> : <span className="faint">—</span>}</td>
                        <td className="num tabular hide-sm">{i.perMonth ? qtyText(Math.round(i.perMonth * 10) / 10) : <span className="faint">—</span>}</td>
                        <td className="num tabular hide-sm">{i.daysLeft != null ? `${fmtNumber(i.daysLeft)} ימים` : <span className="faint">—</span>}</td>
                        <td><span className={`badge ${STATUS[i.status].cls}`}>{STATUS[i.status].label}</span></td>
                        {showCost ? <td className="num tabular hide-sm muted">{i.cost == null ? '—' : fmtMoney(i.cost)}</td> : null}
                        {showCost ? <td className="num tabular">{i.value == null ? <span className="faint">—</span> : fmtMoney(i.value, { round: true })}</td> : null}
                        <td className="num"><button type="button" className="link-button">פרטים</button></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {data?.unmatched.length ? <UnmatchedLines lines={data.unmatched} /> : null}
      </div>
      {open ? <ProductStock item={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

/** Lines of documents since the count that are not a catalog product — so nothing left the stock for them. */
function UnmatchedLines({ lines }: { lines: Inventory['unmatched'] }) {
  const openDoc = useOpenDocument();
  return (
    <section className="panel">
      <div className="panel-head row wrap" style={{ gap: 10 }}>
        <h2 style={{ margin: 0 }}>שורות שלא ירדו מהמלאי</h2>
        <span className="muted small">שורות במסמכים מאז הספירה שאינן מוצר בקטלוג. שיוך למוצר מוריד אותו מהמלאי אוטומטית.</span>
        <span className="spacer" />
        <Link to="/products/matching" className="btn sm">שיוך שורות למוצרים</Link>
      </div>
      <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>כפי שמופיע במסמך</th>
              <th className="num">כמות</th>
              <th className="num">מסמכים</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.text}>
                <td className="primary">{l.text}</td>
                <td className="num tabular">{qtyText(l.qty)}</td>
                <td className="num tabular">{fmtNumber(l.documents)}</td>
                <td className="num"><button type="button" className="link-button" onClick={() => openDoc(l.lastDocumentId)}>למסמך</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** One product: its movements (each with its source), a correction by hand, and the alert minimum. */
function ProductStock({ item, onClose }: { item: Item; onClose: () => void }) {
  const { data, error, refetch } = useQuery({ queryKey: ['inventory', 'movements', item.productId], queryFn: () => api<{ items: Movement[] }>(`/api/inventory/${item.productId}/movements`) });
  const qc = useQueryClient();
  const toast = useToast();
  const openDoc = useOpenDocument();
  const [mode, setMode] = useState<'set' | 'add'>('set');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [min, setMin] = useState(item.min != null ? String(item.min) : '');
  const [busy, setBusy] = useState<string | null>(null);
  const current = qc.getQueryData<Inventory>(['inventory'])?.items.find((i) => i.productId === item.productId) ?? item;

  const adjust = async () => {
    const n = num(qty);
    if (n == null || !Number.isFinite(n)) return;
    setBusy('adjust');
    try {
      await api(`/api/inventory/${item.productId}/adjust`, { body: { mode, qty: n, note: note.trim() || null } });
      toast.success('המלאי עודכן');
      setQty('');
      setNote('');
      qc.invalidateQueries({ queryKey: ['inventory'] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const saveMin = async () => {
    const n = num(min);
    setBusy('min');
    try {
      await api(`/api/inventory/${item.productId}/min`, { method: 'PUT', body: { min: n } });
      toast.success(n == null ? 'מינימום אוטומטי' : 'המינימום נשמר');
      qc.invalidateQueries({ queryKey: ['inventory'] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open onClose={onClose} title={item.name} size="lg">
      <div className="stack">
        <div className="row wrap" style={{ gap: 18 }}>
          <div><div className="muted small">במלאי</div><div style={{ fontSize: 26, fontWeight: 800 }}>{qtyText(current.qty)}</div></div>
          <div><div className="muted small">מכירות בחודש</div><div style={{ fontSize: 18, fontWeight: 700 }}>{current.perMonth ? qtyText(Math.round(current.perMonth * 10) / 10) : '—'}</div></div>
          <div><div className="muted small">יספיק ל-</div><div style={{ fontSize: 18, fontWeight: 700 }}>{current.daysLeft != null ? `${fmtNumber(current.daysLeft)} ימים` : '—'}</div></div>
          {current.value != null ? <div><div className="muted small">שווי</div><div style={{ fontSize: 18, fontWeight: 700 }}>{fmtMoney(current.value, { round: true })}</div></div> : null}
          <span className="spacer" />
          <span className="muted small"><Ltr>{item.sku}</Ltr>{item.unitsPerPack ? ` · ${item.unitsPerPack} יח׳ בחבילה (6 יח׳ = חצי חבילה)` : ''}</span>
        </div>

        <div className="row wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
          <Field label="תיקון ידני">
            <div className="row" style={{ gap: 6 }}>
              <Select value={mode} onChange={(e) => setMode(e.target.value as 'set' | 'add')} style={{ width: 130 }}>
                <option value="set">הכמות היא</option>
                <option value="add">להוסיף / להוריד</option>
              </Select>
              <Input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" ltr style={{ width: 90 }} placeholder={mode === 'add' ? '‎-2 / 5' : '0'} aria-label="כמות" />
            </div>
          </Field>
          <Field label="סיבה">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="נשבר, דוגמה ללקוח, נמצא…" style={{ width: 220 }} />
          </Field>
          <Button loading={busy === 'adjust'} disabled={num(qty) == null} onClick={adjust}>עדכון</Button>
          <span className="spacer" />
          <Field label="מינימום להתראה" hint={item.autoMin != null ? `ריק = אוטומטי (${qtyText(item.autoMin)})` : 'ריק = שבועיים של מכירות'}>
            <div className="row" style={{ gap: 6 }}>
              <Input value={min} onChange={(e) => setMin(e.target.value)} inputMode="decimal" ltr style={{ width: 80 }} />
              <Button size="sm" loading={busy === 'min'} onClick={saveMin}>שמירה</Button>
            </div>
          </Field>
        </div>

        <h3 style={{ margin: '6px 0 0' }}>תנועות</h3>
        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <div className="table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>תאריך</th>
                  <th>מה</th>
                  <th className="num">שינוי</th>
                  <th className="num">יתרה</th>
                  <th className="hide-sm">מי</th>
                </tr>
              </thead>
              <tbody>
                {!data ? (
                  <SkeletonRows rows={4} cols={5} />
                ) : data.items.length === 0 ? (
                  <tr><td colSpan={5}><Empty title="עוד אין תנועות" body="המוצר ייכנס למלאי בספירה" /></td></tr>
                ) : (
                  data.items.map((m) => (
                    <tr key={m.id}>
                      <td className="tabular">{fmtDateTime(m.at)}</td>
                      <td>
                        {KIND[m.kind]}
                        {m.documentId ? (
                          <span className="sub">
                            <button type="button" className="link-button" onClick={() => openDoc(m.documentId!)}>
                              {DOC[m.docType ?? ''] ?? 'מסמך'} {m.docNumber ?? ''}
                            </button>
                            {m.customerName ? ` · ${m.customerName}` : ''}
                          </span>
                        ) : m.countName ? (
                          <span className="sub">{m.countName}</span>
                        ) : null}
                        {m.note ? <span className="sub">{m.note}</span> : null}
                      </td>
                      <td className="num tabular" style={{ color: m.qty > 0 ? 'var(--ok)' : m.qty < 0 ? 'var(--bad)' : undefined }}>
                        <bdi dir="ltr">{m.qty > 0 ? '+' : ''}{qtyText(m.qty)}</bdi>
                      </td>
                      <td className="num tabular strong">{qtyText(m.balance)}</td>
                      <td className="hide-sm muted">{m.userName ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Dialog>
  );
}

type CountSummary = { id: number; name: string; status: 'open' | 'applied' | 'cancelled'; lines: number; createdAt: string; appliedAt: string | null; createdBy: string | null; summary: { products: number; changed: number; valueBefore: number | null; valueAfter: number | null } | null };

/** Counts so far. */
export function StockCountsPage() {
  const { data, error, refetch } = useQuery({ queryKey: ['inventory', 'counts'], queryFn: () => api<{ items: CountSummary[] }>('/api/inventory/counts') });
  const can = useCan();
  const navigate = useNavigate();
  const toast = useToast();
  const STATE = { open: 'פתוחה', applied: 'אושרה', cancelled: 'בוטלה' };
  const open = data?.items.find((c) => c.status === 'open');
  const start = async () => {
    try {
      const r = await api<{ id: number }>('/api/inventory/counts', { body: {} });
      navigate(`/inventory/count/${r.id}`);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs">{can('inventory.edit') ? <Link to="/inventory">מלאי</Link> : 'מלאי'} / ספירות</div>
          <h1>ספירות מלאי</h1>
        </div>
        {data ? <Button variant="primary" onClick={start}>{open ? `המשך ספירה: ${open.name}` : 'ספירה חדשה'}</Button> : null}
      </div>
      <section className="panel">
        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>ספירה</th>
                  <th>מצב</th>
                  <th className="num">מוצרים שנספרו</th>
                  <th className="num hide-sm">השתנו</th>
                  <th className="hide-sm">נפתחה</th>
                  <th className="hide-sm">אושרה</th>
                </tr>
              </thead>
              <tbody>
                {!data ? (
                  <SkeletonRows rows={3} cols={6} />
                ) : data.items.length === 0 ? (
                  <tr><td colSpan={6}><Empty title="עוד לא נעשתה ספירה" /></td></tr>
                ) : (
                  data.items.map((c) => (
                    <tr key={c.id}>
                      <td className="primary">
                        <Link to={`/inventory/count/${c.id}`} style={{ color: 'var(--ink)' }}>{c.name}</Link>
                        {c.createdBy ? <span className="sub">{c.createdBy}</span> : null}
                      </td>
                      <td><span className={`badge ${c.status === 'open' ? 'info' : c.status === 'applied' ? 'ok' : 'outline'}`}>{STATE[c.status]}</span></td>
                      <td className="num tabular">{fmtNumber(c.summary?.products ?? c.lines)}</td>
                      <td className="num tabular hide-sm">{c.summary ? fmtNumber(c.summary.changed) : '—'}</td>
                      <td className="hide-sm">{fmtDate(c.createdAt)}</td>
                      <td className="hide-sm">{c.appliedAt ? fmtDate(c.appliedAt) : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
